import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';

const LOGO_URL = 'https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG';

/** localStorage key holding the persisted desktop collapse state. */
export const DRAWER_COLLAPSED_KEY = 'bmlc_drawer_collapsed';

/**
 * Collapse state for the desktop rail, persisted in localStorage.
 * Lifted into <Layout /> so the content column padding can follow the
 * drawer width with the same transition.
 */
export const useDrawerCollapsed = (): [boolean, () => void] => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(DRAWER_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(DRAWER_COLLAPSED_KEY, next ? 'true' : 'false');
      } catch {
        /* storage unavailable (private mode) — keep the in-memory state */
      }
      return next;
    });
  }, []);

  return [collapsed, toggleCollapsed];
};

interface NavItem {
  label: string;
  path: string;
  /** svg path `d` values, rendered as stroked outline icons */
  paths: string[];
}

const DASHBOARD_ITEM: NavItem = {
  label: 'Dashboard',
  path: '/admin/dashboard',
  paths: [
    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  ],
};

const DOCUMENTS_ITEM: NavItem = {
  label: 'Documents',
  path: '/admin/documents',
  paths: [
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  ],
};

const STUDENTS_ITEM: NavItem = {
  label: 'Students',
  path: '/admin/students',
  paths: [
    'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  ],
};

/** Staff management — shield icon, distinct from the Students user-group icon. */
const TEAM_ITEM: NavItem = {
  label: 'Team',
  path: '/admin/staff',
  paths: [
    'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  ],
};

const CHAT_MONITORING_ITEM: NavItem = {
  label: 'Chat Monitoring',
  path: '/admin/chats',
  paths: [
    'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  ],
};

/**
 * Staff use the same /chat page as students. Their questions are excluded from
 * analytics and Chat Monitoring, which only report on role = 'user'.
 */
const AI_CHAT_ITEM: NavItem = {
  label: 'AI Chat',
  path: '/chat',
  paths: [
    'M13 10V3L4 14h7v7l9-11h-7z',
  ],
};

const ADMIN_ITEMS: NavItem[] = [
  DASHBOARD_ITEM,
  DOCUMENTS_ITEM,
  STUDENTS_ITEM,
  TEAM_ITEM,
  CHAT_MONITORING_ITEM,
  AI_CHAT_ITEM,
];

const FACULTY_ITEMS: NavItem[] = [DOCUMENTS_ITEM, AI_CHAT_ITEM];

const USER_ITEMS: NavItem[] = [
  {
    label: 'Chat',
    path: '/chat',
    paths: [
      'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    ],
  },
];

interface DrawerProps {
  /** desktop icon-rail mode */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** off-canvas state below `lg` */
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

const Drawer: React.FC<DrawerProps> = ({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getUser();
  const isSuperAdmin = authService.isSuperAdmin();
  const isFaculty = authService.isFaculty();

  const items = useMemo<NavItem[]>(() => {
    if (isSuperAdmin) return ADMIN_ITEMS;
    if (isFaculty) return FACULTY_ITEMS;
    return USER_ITEMS;
  }, [isSuperAdmin, isFaculty]);

  const sectionLabel = isSuperAdmin ? 'Administration' : isFaculty ? 'Faculty' : 'Navigation';

  const homePath = isSuperAdmin ? '/admin/dashboard' : isFaculty ? '/admin/documents' : '/chat';

  /* Route-prefix based active detection */
  const isActive = useCallback(
    (path: string) => {
      const current = location.pathname;
      return current === path || current.startsWith(`${path}/`);
    },
    [location.pathname]
  );

  /* Escape closes the off-canvas drawer */
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMobile();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onCloseMobile]);

  /* Body scroll lock while the off-canvas drawer is open */
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  /* Tracks the lg breakpoint: growing past it hands over to the fixed sidebar,
     and it tells us whether the off-screen panel must be hidden from AT. */
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handleChange = () => setIsDesktop(mq.matches);
    handleChange();
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (mobileOpen && isDesktop) onCloseMobile();
  }, [mobileOpen, isDesktop, onCloseMobile]);

  const go = (path: string) => {
    onCloseMobile();
    navigate(path);
  };

  const handleLogout = () => {
    onCloseMobile();
    authService.logout();
    navigate('/login');
  };

  /* Labels stay visible on mobile; only the lg rail hides them. */
  const labelHidden = collapsed ? 'lg:hidden' : '';
  const initial = user?.username?.charAt(0).toUpperCase() || 'U';
  const roleLabel = isSuperAdmin ? 'Super Admin' : isFaculty ? 'Faculty' : 'Student';

  const tooltip = (label: string) => (
    <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-bmlc-navy-900 px-2.5 py-1.5 text-xs font-semibold text-white opacity-0 shadow-soft-lg transition-opacity duration-150 lg:block lg:group-hover:opacity-100">
      {label}
    </span>
  );

  return (
    <>
      {/* Blurred navy overlay (mobile / tablet only) */}
      <div
        aria-hidden="true"
        onClick={onCloseMobile}
        className={`fixed inset-0 z-40 bg-bmlc-navy-900/60 backdrop-blur-sm transition-opacity duration-300 ease-out-soft lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      {/* Below lg the panel is only translated off-screen, which does NOT remove
          it from the tab order — hide it from AT and keyboard while closed.
          isDesktop keeps the always-visible desktop sidebar fully interactive. */}
      <aside
        aria-label="Main navigation"
        aria-hidden={!mobileOpen && !isDesktop ? true : undefined}
        {...(!mobileOpen && !isDesktop ? { inert: '' } : {})}
        className={`fixed inset-y-0 left-0 z-50 flex w-[16.5rem] max-w-[85vw] flex-col border-r border-bmlc-navy-100 bg-white/95 shadow-soft-xl backdrop-blur-xl transition-[transform,width] duration-300 ease-out-soft lg:translate-x-0 lg:shadow-soft-sm ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'}`}
      >
        {/* Gold hairline down the right edge */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-bmlc-gold-500/60 to-transparent"
        />

        {/* Desktop collapse toggle */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          className="absolute -right-3 top-8 z-20 hidden h-6 w-6 items-center justify-center rounded-full border border-bmlc-navy-100 bg-white text-bmlc-navy-500 shadow-soft-sm transition-all duration-200 ease-out-soft hover:border-bmlc-gold-400 hover:text-bmlc-gold-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400 lg:flex"
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform duration-300 ease-out-soft ${
              collapsed ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {/* Brand lockup */}
        <div className={`flex items-center gap-2 px-4 pb-3 pt-5 ${collapsed ? 'lg:justify-center lg:px-2' : ''}`}>
          <button
            type="button"
            onClick={() => go(homePath)}
            className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400"
          >
            <img
              src={LOGO_URL}
              alt="BM Law College Logo"
              className="h-11 w-11 flex-shrink-0 rounded-full bg-white object-contain shadow-gold-glow ring-2 ring-bmlc-gold-300 transition-transform duration-300 ease-out-soft group-hover:scale-105"
            />
            <span className={`min-w-0 leading-tight ${labelHidden}`}>
              <span className="block truncate font-serif text-[15px] font-bold tracking-tight text-bmlc-navy-900">
                BM Law College
              </span>
              <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-bmlc-gold-600">
                AI Legal Assistant
              </span>
            </span>
          </button>

          {/* Close (mobile only) */}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close navigation"
            className="btn-ghost flex-shrink-0 rounded-lg p-2 text-bmlc-navy-500 lg:hidden"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="divider-gold my-0" />

        {/* Nav sections */}
        <nav
          className={`min-h-0 flex-1 overflow-y-auto px-3 py-4 ${
            collapsed ? 'lg:overflow-visible' : ''
          }`}
        >
          <p
            className={`px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 ${labelHidden}`}
          >
            {sectionLabel}
          </p>

          <ul className="space-y-1">
            {items.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => go(item.path)}
                    aria-current={active ? 'page' : undefined}
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-200 ease-out-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400 ${
                      collapsed ? 'lg:justify-center lg:px-0' : ''
                    } ${
                      active
                        ? 'bg-navy-sheen text-white shadow-soft-sm'
                        : 'text-bmlc-navy-700 hover:bg-bmlc-navy-50 hover:text-bmlc-navy-900'
                    }`}
                  >
                    {active && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-gold-sheen"
                      />
                    )}
                    <svg
                      className={`h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
                        active ? 'text-bmlc-gold-300' : 'text-bmlc-navy-400 group-hover:text-bmlc-navy-600'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {item.paths.map((d) => (
                        <path key={d} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
                      ))}
                    </svg>
                    <span className={`truncate ${labelHidden}`}>{item.label}</span>
                    {collapsed && tooltip(item.label)}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User chip + logout */}
        <div className="mt-auto px-3 pb-4">
          <div className="divider-gold my-0 mb-3" />
          <div
            className={`flex items-center gap-3 rounded-xl border border-bmlc-navy-100 bg-bmlc-navy-50/70 p-2.5 ${
              collapsed ? 'lg:flex-col lg:gap-2 lg:border-transparent lg:bg-transparent lg:p-0' : ''
            }`}
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gold-sheen font-serif text-sm font-bold text-white shadow-gold-glow ring-2 ring-bmlc-gold-200 ring-offset-2 ring-offset-white">
              {initial}
            </div>
            <div className={`min-w-0 flex-1 leading-tight ${labelHidden}`}>
              <p className="truncate text-sm font-semibold text-bmlc-navy-900">
                {user?.username || 'Guest'}
              </p>
              <p className="truncate text-[11px] tracking-wide text-gray-500">{roleLabel}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Logout"
              title="Logout"
              className="btn-ghost group relative flex-shrink-0 rounded-lg p-2 text-gray-500 hover:bg-bmlc-maroon-50 hover:text-bmlc-maroon-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {collapsed && tooltip('Logout')}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Drawer;
